import React, { useRef, useState } from 'react';
import { Box } from '@mui/system';
import {
  Button,
  Container,
  Typography,
  CircularProgress,
  Dialog,
  DialogContentText,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { DocumentData } from '@google-cloud/firestore';
import { FormTextField } from '../custom/CustomComponents';
import { AttachFile, Delete, PictureAsPdf, Print } from '@mui/icons-material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { FormIds, submitForm } from '../../services';

export interface IRequisitionFormData {
  requestedBy: string;
  ministry: string;
  requestDate: string;
  dateNeeded: string;
  budgetedAmount: number;
  amountRequested: number;
  checkPayableTo: string;
  reasonForRequest: string;
  submittedBy: string;
  ministryLeader: string;
  trusteeSignature: string;
  receipts: string[];
}

function getFileTypeFromUrl(url: string): 'image' | 'pdf' | 'unknown' {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(path)) return 'image';
    if (/\.pdf$/i.test(path)) return 'pdf';
  } catch (_) {
    return 'unknown';
  }
  return 'unknown';
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const RequisitionForm = ({ form = null }: { form?: DocumentData | null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<IRequisitionFormData>({
    ministry: form?.ministry ?? '',
    requestDate: form?.requestDate ?? formatDate(new Date()),
    requestedBy: form?.requestedBy ?? '',
    dateNeeded: form?.dateNeeded ?? formatDate(new Date()),
    budgetedAmount: form?.budgetedAmount ?? '',
    amountRequested: form?.amountRequested ?? '',
    checkPayableTo: form?.checkPayableTo ?? '',
    reasonForRequest: form?.reasonForRequest ?? '',
    submittedBy: form?.submittedBy ?? '',
    ministryLeader: form?.ministryLeader ?? '',
    trusteeSignature: form?.trusteeSignature ?? '',
    receipts: form?.receipts ?? [],
  });

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    setReceiptFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setReceiptFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadReceipts = async (files: File[]): Promise<string[]> => {
    const storage = getStorage();
    return Promise.all(
      files.map(async (file) => {
        const storageRef = ref(
          storage,
          `requisition-receipts/${Date.now()}-${file.name}`
        );
        await uploadBytesResumable(storageRef, file);
        return getDownloadURL(storageRef);
      })
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const receiptUrls = await uploadReceipts(receiptFiles);
      await submitForm(FormIds.requisitionForm, {
        ...formData,
        receipts: receiptUrls,
      });
      setShowModal(true);
    } catch {
      alert('Error Submitting');
    } finally {
      setIsLoading(false);
    }
  };
  const { ministry, requestedBy, reasonForRequest, amountRequested } = formData;
  const isDisabled = !(
    ministry &&
    requestedBy &&
    reasonForRequest &&
    amountRequested
  );

  const formDisabled = form !== null;

  const print = () => {
    window.print();
  };
  return (
    <Box sx={{ margin: '2rem' }}>
      <Container sx={{ width: { md: '50%', xs: '100%' } }}>
        <Typography variant="h5" align="center" gutterBottom>
          NEW MT. ZION MISSIONARY BAPTIST CHURCH LAKELAND CHECK AND PURCHASE
          REQUISITION FORM
        </Typography>

        {formDisabled && (
          <div className="no-print">
            <Button variant="contained" startIcon={<Print />} onClick={print}>
              Print Completed Form
            </Button>
          </div>
        )}

        <Box sx={{ borderTop: '2px black solid', pt: 2, mt: 2 }}>
          <Typography variant="body1" align="left" paragraph>
            This form accurately and responsibly documents the flow of church
            funds for purchases and check distribution. Receipts and the
            appropriate documentation are submitted within 48 hours of all
            purchases to explain how and why funds are used. All purchases are
            made within the operating budget guidelines, and special review and
            approval are required for all unbudgeted expenditures and
            expenditures that exceed the budgeted amount. New Mt. Zion is
            tax-exempt, and the church’s tax identification number is used for
            all purchases. The Trustee Ministry and The Business Manager review
            and approve requisitions in a timely manner. Pastoral input is
            obtained when needed.{' '}
          </Typography>
        </Box>
        <Box sx={{ borderBottom: '2px black solid', pt: 2, mt: 2, mb: 2 }}>
          <Typography variant="h6">CHECK AND PURCHASE GUIDELINES</Typography>
          <Typography>
            <ol>
              <li>
                A requisition form must be submitted for all the funds the
                church releases. Electronic submission of forms is encouraged.
              </li>
              <li>
                The ministry leader must sign and submit all requests based on
                allocated budgeted funds.
              </li>
              <li>
                Invoices, receipts, and packing sheets must be submitted within
                48 hours for all purchases and are attached to the requisition
                form.
              </li>
              <li>Cash advances are approved in rare instances.</li>
              <li>
                Vendor estimates and order forms must be approved in advance. A
                vendor checklist is required to verify licensure and insurance.
                All checks will be issued in the vendor’s business name.
              </li>
              <li>
                All honorariums are paid by check with the appropriate
                documentation (includes W-9)
              </li>
            </ol>
          </Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Requested By"
            name="requestedBy"
            value={formData.requestedBy}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Ministry"
            name="ministry"
            value={formData.ministry}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Date"
            name="requestDate"
            type="date"
            value={formData.requestDate}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Date Needed"
            name="dateNeeded"
            type="date"
            value={formData.dateNeeded}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Budget Amount"
            name="budgetedAmount"
            value={formData.budgetedAmount}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Amount Requested"
            name="amountRequested"
            value={formData.amountRequested}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Check Payable to"
            name="checkPayableTo"
            value={formData.checkPayableTo}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Reason for Request"
            name="reasonForRequest"
            value={formData.reasonForRequest}
            onChange={handleChange}
            multiline
            rows={3}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Submitted By"
            name="submittedBy"
            value={formData.submittedBy}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Ministry Leader"
            name="ministryLeader"
            value={formData.ministryLeader}
            onChange={handleChange}
          />
          <FormTextField
            disabled={formDisabled}
            fullWidth
            margin="normal"
            label="Trustee Signature"
            name="trusteeSignature"
            value={formData.trusteeSignature}
            onChange={handleChange}
          />

          {!form && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Receipts
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
              >
                Attach Receipts
              </Button>
              {receiptFiles.length > 0 && (
                <List dense>
                  {receiptFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => removeFile(index)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={file.name} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {form && formData.receipts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Receipts
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                {formData.receipts.map((url, index) => {
                  const type = getFileTypeFromUrl(url);
                  if (type === 'image') {
                    return (
                      <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                        <Box
                          component="img"
                          src={url}
                          alt={`Receipt ${index + 1}`}
                          sx={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </a>
                    );
                  }
                  return (
                    <a key={index} href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          gap: 1,
                          color: 'error.main',
                        }}
                      >
                        <PictureAsPdf sx={{ fontSize: 48 }} />
                        <Typography variant="caption" color="text.secondary">
                          Receipt {index + 1}
                        </Typography>
                      </Box>
                    </a>
                  );
                })}
              </Box>
            </Box>
          )}

          {!form && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginTop: '1em' }}
              disabled={isDisabled || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          )}

          <Dialog
            open={showModal}
            onClose={() => {}}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Form Submitted</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Form submitted
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </form>
        {/* <Typography variant="body1" align="center" paragraph sx={{ mt: 2 }}>
          Thank you for completing the 2025 Budget Request Form. Please contact
          the administrative office with questions.
        </Typography> */}
      </Container>
    </Box>
  );
};

export default RequisitionForm;
